import React, { useMemo, useState } from "react";
import { getAge, noPhoto } from "@/utils";
import { CameraIcon } from "@/svgs";
import { PlanInfo, PublicPhoto } from "@/services";
import { UserStatus } from "@/components";
import "./UserCard.css";

export interface UserCardData {
  uid: string;
  username: string;
  usernameSlug: string;
  birthYear?: string;
  city?: string;
  state?: string;
  profileImage?: PublicPhoto | string;
  photosCount?: number;
  gender?: "Masculino" | "Feminino";
  isVerified?: boolean;
  planInfo?: PlanInfo;
}

interface Props {
  data: UserCardData;

  isLocked?: boolean;
  isFavorited?: boolean;
  presence?: any;

  onNavigate?: (user: UserCardData) => void;
  onFavorite?: (user: UserCardData) => void;
  onSelect?: (id: string) => void;
  onStartChat?: (user: UserCardData) => void;

  currentUser?: any;
  selected?: boolean;
  favoriteBusyId?: string | null;
}

const UserCard: React.FC<Props> = ({
  data,
  currentUser,
  selected = false,
  favoriteBusyId,
  isLocked = false,
  presence,
  isFavorited = false,
  onNavigate,
  onFavorite,
  onSelect,
  onStartChat,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const birthYear = getAge(data.birthYear);
  const city = data.city || "";
  const state = data.state || "";

  const profileImageUrl = useMemo(() => {
    const img = data.profileImage;

    if (!img || (typeof img === "string" && !img.trim())) {
      return noPhoto(data.gender);
    }

    if (typeof img === "string") return img;

    if (typeof img === "object" && img.approved !== false) {
      return img.url;
    }

    return noPhoto(data.gender);
  }, [data.profileImage, data.gender]);

  const handle = (fn?: any, e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (isLocked) return;
    fn?.(data);
  };

  const handleSelect = () => {
    onSelect?.(data.uid);
  };

  const dynamicAspectRatio = useMemo(() => {
    if (!data.uid) return 1.4;
    const charCodeSum = data.uid
      .split("")
      .reduce((acc: any, char: string) => acc + char.charCodeAt(0), 0);
    const variations = [1.1, 1.3, 1.45, 1.6, 1.75];
    return variations[charCodeSum % variations.length];
  }, [data.uid]);

  return (
    <article
      className={`user-card ${selected ? "is-selected" : ""} ${
        !imageLoaded ? "is-loading" : ""
      } ${isLocked ? "is-locked" : ""}`}
      style={{
        flexGrow: dynamicAspectRatio,
        flexBasis: `${dynamicAspectRatio * 160}px`,
        maxWidth: `${dynamicAspectRatio * 280}px`,
      }}
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleSelect();
      }}
    >
      <img
        src={profileImageUrl}
        onLoad={() => setImageLoaded(true)}
        style={{ display: "none" }}
        alt=""
      />

      <div
        className="user-card__image"
        style={{ backgroundImage: `url(${profileImageUrl})` }}
      >
        <div className="user-card__top">
          <div className="pill-group">
            <UserStatus
              state={presence?.state}
              lastActivity={presence?.lastActivity}
            />

            {data.planInfo?.active && (
              <span className="pill pill-premium">★</span>
            )}

            {data.isVerified && <span className="pill pill-verified">✔</span>}
          </div>

          <div className="user-card__photos">
            <CameraIcon />
            <span>{data.photosCount || 0}</span>
          </div>
        </div>

        {isLocked ? (
          <div className="user-card__lock">
            <div className="lock-circle">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lock-icon"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <strong className="lock-title">Perfil Premium</strong>
            <span className="lock-subtitle">Seja premium para ver</span>
          </div>
        ) : (
          <div className="user-card__bottom">
            <div className="user-card__info">
              <div className="user-card__name">
                {data.username}, {birthYear}
              </div>
              <div className="user-card__location">
                {city}
                {city && state ? ", " : ""}
                {state}
              </div>
            </div>

            <div className="user-card__actions">
              <button
                onClick={(e) => handle(onNavigate, e)}
                className="icon-btn"
                aria-label="Acessar perfil"
                title="Acessar perfil"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>

              <button
                onClick={(e) => handle(onStartChat, e)}
                className="icon-btn"
                disabled={favoriteBusyId === data.uid}
                aria-label="Iniciar chat"
                title="Iniciar chat"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                </svg>
              </button>

              <button
                onClick={(e) => handle(onFavorite, e)}
                className={`icon-btn ${isFavorited ? "is-favorited" : ""}`}
                disabled={favoriteBusyId === data.uid}
                aria-label="Favoritar"
                title="Favoritar"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill={isFavorited ? "currentColor" : "none"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default UserCard;
